import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import type { Project } from '@/api/core-api/types.gen';

import { ProjectStatus } from '@/api/core-api';
import { notify } from '@/components/base/notifications/notifications';
import { useCreditCards } from '@/hooks/api/use-credit-cards';
import { usePricingMultiplier } from '@/hooks/api/use-pricing-multiplier';
import { useResendVerificationEmail } from '@/hooks/api/use-resend-verification-email';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { orgFeatureFlagQueryOptions } from '@/lib/feature-flags/client/org-feature-flag-query-options';
import { getCurrentSessionOptions } from '@/utils/session/session-query-options';

import { ProjectFlowLayout } from '../-project-flow-layout';
import { ALLOW_ZERO_SCREENER_QUESTIONS_FLAG } from '../-screener/screener-constants';
import {
  pricingMultiplierAttributes,
  buildProjectPublishClickedAttributes,
  projectLaunchedTrackPayload,
} from './-launch/launch-analytics';
import { LaunchConfirmModal } from './-launch/launch-confirm-modal';
import { LaunchContent } from './-launch/launch-content';
import { getLaunchGate, isEffectiveCardExpired } from './-launch/launch-gate';
import {
  isApprovedToPublish,
  validateForLaunch,
  type LaunchValidationError,
} from './-launch/launch-utils';
import { preflightLaunchCheck } from './-launch/preflight-launch';
import { amountToPayNowToDisplay } from './-launch/pricing-display-utils';
import { ALLOW_OPTIONAL_TOPICS_FLAG } from './-launch/targeting-constants';
import { teamPlanSummaryQueryOptions } from './-launch/team-pricing-version';
import {
  useLaunchProject,
  type LaunchError,
} from './-launch/use-launch-project';
import { projectRequirementsQueryOptions } from './-launch/use-project-requirements';
import { useParticipantLimit } from './-participant-limit/use-participant-limit';
import { researcherProjectQueryOptions } from './-shared';

export const Route = createFileRoute(
  '/researchers/_authenticated/projects/$projectId/launch',
)({
  // Launching is only meaningful for draft projects. Once the project has
  // moved past DRAFT, send the user to the read-only details page rather than
  // letting them re-trigger the launch flow.
  beforeLoad: async ({ params, context }) => {
    const response = await context.queryClient.ensureQueryData(
      researcherProjectQueryOptions(params.projectId, context.user.id),
    );
    if (!response.success) return { projectName: null };
    if (response.data.status !== ProjectStatus.DRAFT) {
      throw redirect({
        to: '/researchers/projects/$projectId/details',
        params: { projectId: params.projectId },
      });
    }
    return { projectName: response.data.name };
  },
  head: ({ match }) => ({
    meta: [{ title: t`Launch · ${match.context.projectName ?? 'project'}` }],
  }),
  component: LaunchProjectPage,
});

function LaunchProjectPage() {
  const { projectId } = Route.useParams();
  const { data: projectResponse, isLoading } = useQuery(
    researcherProjectQueryOptions(projectId),
  );

  const project =
    projectResponse?.success === true ? projectResponse.data : null;

  if (isLoading) {
    return (
      <ProjectFlowLayout currentStep="recruit" projectId={projectId}>
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sm text-tertiary">
            <Trans>Loading project...</Trans>
          </p>
        </div>
      </ProjectFlowLayout>
    );
  }

  if (!project) {
    return (
      <ProjectFlowLayout currentStep="recruit" projectId={projectId}>
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sm text-error-primary">
            {projectResponse?.success === false
              ? projectResponse.error
              : t`Project not found`}
          </p>
        </div>
      </ProjectFlowLayout>
    );
  }

  return <LaunchProjectContent project={project} />;
}

// Stable-ish key for deduping validation errors
const fieldKey = (errors: LaunchValidationError[]) =>
  Array.from(new Set(errors.map((e) => e.field)))
    .toSorted()
    .join(',');

function LaunchProjectContent({ project }: { project: Project }) {
  const navigate = useNavigate();
  const analytics = useAnalytics();
  // PX-453: with the organization on the allow-zero flag a screener is not
  // required to launch. The server resolves the same flag at publish, so the
  // client rule and the 400 it pre-empts agree.
  const { data: allowZeroScreenerQuestions = false } = useQuery(
    orgFeatureFlagQueryOptions(
      project.organizationId,
      ALLOW_ZERO_SCREENER_QUESTIONS_FLAG,
    ),
  );
  // PX-456: with the organization on the optional-topics flag a B2C project
  // does not need topics to launch. The server resolves the same flag at
  // publish, so the client rule and the 400 it pre-empts agree.
  const { data: allowOptionalTopics = false } = useQuery(
    orgFeatureFlagQueryOptions(
      project.organizationId,
      ALLOW_OPTIONAL_TOPICS_FLAG,
    ),
  );
  // Launch waits for the limit so an over-limit count shows as a launch
  // checklist error instead of a failed prepay.
  const { data: participantLimit, isLoading: isParticipantLimitLoading } =
    useParticipantLimit(project);
  const launchMutation = useLaunchProject(project.teamId, project.id, {
    allowZeroScreenerQuestions,
    allowOptionalTopics,
    participantLimit,
  });
  const resendVerificationMutation = useResendVerificationEmail();
  const { data: session } = useQuery(getCurrentSessionOptions);
  const { data: pricingInfo } = useQuery(
    teamPlanSummaryQueryOptions(project.teamId),
  );
  const {
    data: requirements,
    isLoading: isLoadingRequirements,
    isError: isRequirementsError,
  } = useQuery(projectRequirementsQueryOptions(project.teamId, project.id));
  const { data: pricingMultiplier, isFetched: isPricingMultiplierFetched } =
    usePricingMultiplier(project.id);
  const [showConfirm, setShowConfirm] = useState(false);
  // A saved card picked in the payment sidebar for this launch's charge. Sent
  // as `creditCardToken` so the team default is neither used nor changed.
  const [selectedCardToken, setSelectedCardToken] = useState<string>();
  // Only a prepayment can charge the picked card (see useLaunchProject); a
  // billed-later launch is charged to the team default by autopay, so the
  // selection is neither offered nor honoured there.
  const requiresPrepayment = !!requirements?.prepayment?.requiresPayment;
  const prepaymentCardToken = requiresPrepayment
    ? selectedCardToken
    : undefined;
  // Only to judge the picked card's expiry; `requirements` knows the default's.
  const { data: creditCards } = useCreditCards(project.teamId);

  // ── Launch page view — last step before conversion. Wait for the
  // multiplier preview so the event carries the cost context; fire anyway
  // once the query settles (success or error) so a failed fetch cannot
  // swallow the pageview. audience_estimate is computed lower in the tree
  // and isn't available here, so it's deferred.
  const launchViewFiredRef = useRef(false);
  useEffect(() => {
    if (!analytics || launchViewFiredRef.current) return;
    if (!isPricingMultiplierFetched) return;
    launchViewFiredRef.current = true;
    analytics.track({
      action: 'viewed',
      actionSubject: 'launchPage',
      attributes: {
        project_type: project.kindOfResearch ?? null,
        ...pricingMultiplierAttributes(pricingMultiplier),
      },
    });
  }, [
    analytics,
    project.kindOfResearch,
    isPricingMultiplierFetched,
    pricingMultiplier,
  ]);

  const amountToPayNow = requirements
    ? amountToPayNowToDisplay(requirements, pricingInfo)
    : 0;
  // Researchers at/above APPROVED publish directly; others submit for manual
  // review. Mirrors the backend `qualifyForProjectApproval` gate (trustLevel
  // >= APPROVED); unknown is treated as NOT approved — see isApprovedToPublish.
  const isApproved = isApprovedToPublish(session?.user?.trustLevel);
  // Guards against duplicate resends: the toast's confirm button stays
  // clickable, and its onConfirm closes over a render-time snapshot of the
  // mutation, so `isPending` on that snapshot never updates. A ref reflects
  // live in-flight state across clicks.
  const isResendingVerificationRef = useRef(false);

  const validationErrors = useMemo(
    () =>
      validateForLaunch(project, {
        allowZeroScreenerQuestions,
        allowOptionalTopics,
        participantLimit,
      }),
    [
      project,
      allowZeroScreenerQuestions,
      allowOptionalTopics,
      participantLimit,
    ],
  );
  const [hasLocalErrors, setHasLocalErrors] = useState(false);

  const isNotDraft = project.status !== 'DRAFT';

  // Upstream (non-payment) blockers the header CTA owns directly: a non-draft
  // project, failed launch-form validation, unsaved local errors, or the
  // participant limit not loaded yet.
  const hasUpstreamErrors =
    isNotDraft ||
    validationErrors.length > 0 ||
    hasLocalErrors ||
    isParticipantLimitLoading;

  // Run the SAME gate the PaymentSidebar uses so the header CTA and the sidebar
  // button agree — notably it also blocks launch when a required card is
  // missing or expired, and while requirements/pricing are still loading.
  const requirementsReady =
    !isLoadingRequirements && !!requirements && !isRequirementsError;
  const launchGate = getLaunchGate({
    isNotDraft,
    isRequirementsError,
    requirementsReady,
    hasPricing: !!pricingInfo,
    isLaunchDisabled: hasUpstreamErrors,
    amountToPayNow,
    creditCardRequired: !!requirements?.creditCardRequired,
    creditCardOnFile: !!requirements?.creditCardOnFile,
    creditCardExpired: isEffectiveCardExpired({
      cards: creditCards,
      selectedCardToken: prepaymentCardToken,
      defaultCardExpired: !!requirements?.creditCardExpired,
    }),
    isApproved,
  });
  const hasErrors = launchGate.disabled;

  // Track why project launch was blocked. Dedupe by field set so we only
  // fire when a new field enters or leaves — not on every message tweak.
  // Init to '' so landing on the page with errors already present still
  // fires once on mount.
  const prevFieldKeyRef = useRef('');
  useEffect(() => {
    if (validationErrors.length === 0) {
      prevFieldKeyRef.current = '';
      return;
    }
    if (!analytics) return;

    const nextFieldKey = fieldKey(validationErrors);
    if (prevFieldKeyRef.current === nextFieldKey) return;

    analytics.track({
      action: 'blocked',
      actionSubject: 'projectPublish',
      attributes: { projectId: project.id, validationErrors },
    });

    prevFieldKeyRef.current = nextFieldKey;
  }, [validationErrors, analytics, project.id]);

  // Unverified researchers can use the app but can't publish. Surface a toast
  // that lets them resend the verification email without leaving the flow.
  // Shared by the pre-flight guard and the server-side error backstop.
  const showVerifyEmailToast = () => {
    const id = notify({
      color: 'error',
      title: t`Verify your email to publish`,
      description: t`You must verify your email address before publishing a project.`,
      confirmLabel: t`Resend verification email`,
      onConfirm: () => {
        if (isResendingVerificationRef.current) return;
        isResendingVerificationRef.current = true;
        resendVerificationMutation.mutate(undefined, {
          onSuccess: () =>
            notify({
              color: 'success',
              title: t`Verification email sent`,
              description: t`Check your inbox to verify your email address.`,
              hideDismissLabel: true,
            }),
          onError: () =>
            notify({
              color: 'error',
              title: t`Couldn't resend verification email`,
              description: t`Please try again in a few moments.`,
              hideDismissLabel: true,
            }),
          onSettled: () => {
            toast.dismiss(id);
            isResendingVerificationRef.current = false;
          },
        });
      },
    });
  };

  const showPlanPausedToast = () => {
    notify({
      color: 'error',
      title: t`Your team plan is paused`,
      description: t`Reactivate your plan to launch projects.`,
      hideDismissLabel: true,
    });
  };

  const handleLaunch = () => {
    if (isNotDraft) return;

    // Catch knowable blockers client-side and skip the API round-trip. The
    // launch button stays enabled; this guard — not a disabled state — is what
    // prevents the wasted call. Server-side checks remain the backstop.
    const preflight = preflightLaunchCheck({
      isEmailVerified: session?.user?.isEmailVerified ?? true,
      isPlanPaused: !!pricingInfo?.plan?.pausedAt,
    });
    if (preflight.blocked) {
      if (preflight.reason === 'email-not-verified') showVerifyEmailToast();
      else if (preflight.reason === 'team-plan-paused') showPlanPausedToast();

      analytics?.track({
        action: 'blocked',
        actionSubject: 'projectPublish',
        attributes: { projectId: project.id, reason: preflight.reason },
      });
      return;
    }

    // Never charge a card (or submit for manual review) without an explicit
    // confirmation step. Otherwise launch directly. Do not fire
    // `projectPublish.clicked` here: opening the modal is still cancelable
    // and would leave a DRAFT behind a publish event.
    if (amountToPayNow > 0 || !isApproved) {
      setShowConfirm(true);
      return;
    }

    doLaunch();
  };

  const doLaunch = () => {
    // Here and not in the button's handler: this is the researcher committing to the API
    // call, having confirmed the modal or skipped it because none was needed.
    analytics?.track({
      action: 'clicked',
      actionSubject: 'projectPublish',
      attributes: buildProjectPublishClickedAttributes(
        project,
        pricingMultiplier,
      ),
    });

    const launchVariables = { project, creditCardToken: prepaymentCardToken };

    launchMutation.mutate(launchVariables, {
      onSuccess: (launchedProject) => {
        // THE conversion event. core-api returns 200 without publishing when
        // the study goes to review (PENDING), so only PAID is a published
        // study. The per-project AI-acceptance snapshot (ai_*_kept,
        // refined_via_chat), used_ai_generation, audience_estimate and the
        // researcher_lifetime_projects user property are deferred — they
        // require persisting the AI's original output as a baseline.
        const launched = projectLaunchedTrackPayload(
          launchedProject,
          pricingMultiplier,
        );
        if (launched) analytics?.track(launched);

        navigate({
          to: '/researchers/projects/$projectId/details',
          params: { projectId: launchedProject.id },
        });
      },
      onError: (error) => {
        analytics?.track({
          action: 'failed',
          actionSubject: 'projectPublish',
          attributes: {
            projectId: project.id,
            errorCode: error.code,
            errorMessage: error.message,
          },
        });

        // Close the confirmation modal so the sidebar's error alert/toast is
        // visible; the researcher can retry from there.
        setShowConfirm(false);

        // Server-side backstop: if the pre-flight guard was bypassed (e.g.
        // session not yet loaded), still surface the resend toast.
        if (error.code === 'EMAIL_NOT_VERIFIED') {
          showVerifyEmailToast();
        } else {
          const errorMessages: Partial<Record<LaunchError['code'], string>> = {
            TEAM_PLAN_PAUSED:
              'Your team plan is currently paused. Please reactivate your plan to launch projects.',
            USER_INACTIVE:
              'Your account cannot launch projects at this time. Please contact support for assistance.',
            DAILY_LIMIT_REACHED:
              'You have reached the daily publishing limit. Please try again later.',
          };

          notify({
            title: t`Failed to launch project`,
            description:
              error.code in errorMessages
                ? errorMessages[error.code]
                : error.message,
            color: 'error',
          });
        }
      },
    });
  };

  const handleLocalValidationChange = useCallback(
    (errors: boolean) => setHasLocalErrors(errors),
    [],
  );

  // Surface payment-method blockers in the header tooltip too, so a disabled
  // CTA always explains itself — mirroring the sidebar button's dynamic label
  // ("Add/Update payment method to launch").
  const headerWarnings =
    project.status === 'DRAFT'
      ? [
          ...(validationErrors?.map((e) => e.message) ?? []),
          ...(launchGate.state === 'add-card'
            ? [t`Add a payment method to launch.`]
            : launchGate.state === 'update-card'
              ? [t`Update your expired payment method to launch.`]
              : []),
        ]
      : [];

  return (
    <ProjectFlowLayout
      currentStep="recruit"
      projectId={project.id}
      internalName={project.internalName}
      validationWarnings={headerWarnings}
      ctaLabel={isNotDraft ? t`Launched` : t`Launch project`}
      onCtaClick={handleLaunch}
      ctaLoading={launchMutation.isPending}
      ctaDisabled={hasErrors}
      kor={project.kindOfResearch}
    >
      <LaunchContent
        project={project}
        projectId={project.id}
        onLaunch={handleLaunch}
        isLaunching={launchMutation.isPending}
        isLaunchDisabled={hasErrors}
        validationErrors={validationErrors}
        onLocalValidationChange={handleLocalValidationChange}
        selectedCardToken={prepaymentCardToken}
        onSelectCard={requiresPrepayment ? setSelectedCardToken : undefined}
      />
      <LaunchConfirmModal
        isOpen={showConfirm}
        onOpenChange={setShowConfirm}
        amountToPayNow={amountToPayNow}
        isApproved={isApproved}
        isLaunching={launchMutation.isPending}
        onConfirm={doLaunch}
      />
    </ProjectFlowLayout>
  );
}
